import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Eye, Filter as FilterIcon, Mail, Phone, Plug, Trash2, UserPlus } from 'lucide-react'
// Same `react-phone-number-input` formatter the Companies (TenantDetailPage)
// and All-Users (PlatformUserDetailModal) detail views use, so a contact
// number renders identically on every Master-Admin surface.
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { Button } from '../../components/ui/Button'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { lead as leadSchema, leadStatusEnum, leadsResponse } from '../../lib/types'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Select'
import { FilterRow } from '../../components/filters/FilterRow'
import { LeadIntegrationsModal } from '../../components/platform/LeadIntegrationsModal'
import { usePageSize } from '../../lib/display-prefs'

const STATUS_LABEL = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
}

// Pipeline order, earliest stage first — drives the Status picker in the
// details modal. Read straight off the shared response schema rather than
// re-listed here, so the picker can never drift from the statuses the API
// actually accepts.
const STATUS_ORDER = leadStatusEnum.options

// Canonical lead-source taxonomy — mirrors the backend `leads.constants.js`.
const SOURCE_LABEL = {
  manual: 'Manual',
  website: 'Website',
  meta: 'Meta',
  google: 'Google',
  webhook: 'Webhook',
  n8n: 'N8N',
}
const SOURCE_ORDER = ['manual', 'website', 'meta', 'google', 'webhook', 'n8n']

// Token-based tones so every source badge stays legible in light + dark.
const SOURCE_TONE = {
  manual: 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
  website: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  meta: 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
  google: 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  webhook: 'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  n8n: 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
}

function SourceBadge({ source }) {
  const key = source && SOURCE_LABEL[source] ? source : 'manual'
  return (
    <span
      className={
        'inline-block text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ' +
        (SOURCE_TONE[key] ?? SOURCE_TONE.manual)
      }
    >
      {SOURCE_LABEL[key]}
    </span>
  )
}

const STATUS_TONE = {
  new: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)]',
  contacted:
    'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]',
  qualified:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]',
  converted:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]',
  lost: 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
}

/** Two-letter initials for the lead avatar (first + last word). */
function initials(name) {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Render a stored E.164 contact number in human-readable international
 *  form ("+919858582282" → "+91 98585 82282"), matching the Companies and
 *  All-Users detail views. Falls back to the raw value when the number
 *  can't be parsed (corrupt / pre-PhoneInputField rows), and to an em dash
 *  when absent. */
function formatContactNumber(raw) {
  if (!raw) return '—'
  return formatPhoneNumberIntl(raw) || raw
}

/** Website leads store the Contact-form Subject + Message folded into
 *  `notes` as `"Subject: <subject>\n\n<message>"` (the Lead model has no
 *  dedicated fields). Split them back out for display; a lead that isn't
 *  in that shape (e.g. a sales-entered note) falls through to `notes`. */
function parseEnquiry(notes) {
  const text = String(notes ?? '')
  const m = text.match(/^Subject: ([^\n]*)\n\n([\s\S]*)$/)
  if (m) return { subject: m[1] || null, message: m[2] || null, notes: null }
  return { subject: null, message: null, notes: text.trim() || null }
}

/**
 * Master Admin → Leads. A read-only inbox of enquiries captured from the
 * marketing-site Contact form (`source: 'website'`). Leads are created by
 * the public `/leads/public` endpoint — there's no manual "add lead" or
 * per-row edit. Click the eye icon to read the full enquiry (contact
 * number, subject, message); delete is for spam / test rows. The status
 * pipeline pills double as the status filter.
 */
export function LeadsPage() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState('')
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()
  const [viewing, setViewing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [integrationsOpen, setIntegrationsOpen] = useState(false)
  const [bulkDel, setBulkDel] = useState(null) // { ids, clearSelection } | null
  const [bulkRunning, setBulkRunning] = useState(false)

  // Shared server params (Search + Source) for the paged query + export.
  function filterParams() {
    const params = {}
    if (search.trim()) params.search = search.trim()
    if (sourceFilter) params.source = sourceFilter
    return params
  }

  const leadsQuery = useQuery({
    queryKey: ['platform-leads', search, sourceFilter, page, limit],
    queryFn: async () => {
      const res = await api.get('/super-admin/leads', {
        params: { ...filterParams(), page, limit },
      })
      return leadsResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  const sourceCounts = leadsQuery.data?.sourceCounts ?? {}
  // Only offer sources that exist (plus the current selection, so it never
  // vanishes mid-filter). Count shown alongside each label.
  const sourceOptions = SOURCE_ORDER.filter(
    (s) => sourceCounts[s] || s === sourceFilter,
  ).map((s) => ({
    value: s,
    label: sourceCounts[s] ? `${SOURCE_LABEL[s]} (${sourceCounts[s]})` : SOURCE_LABEL[s],
  }))

  // Move a lead along the sales pipeline. The backend already exposed
  // PATCH /super-admin/leads/:id (and audits it as `lead.updated`) — the
  // details modal simply had no control wired to it, so a lead could never
  // leave "New".
  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await api.patch(`/super-admin/leads/${id}`, { status })
      return leadSchema.parse(res.data)
    },
    onSuccess: (data, { status }) => {
      // Keep the open modal in step with what was just saved — the table
      // refetches below, but `viewing` holds its own snapshot of the row.
      setViewing((current) =>
        current ? { ...current, status: data?.status ?? status } : current,
      )
      toast.success('Lead status updated successfully.')
      void qc.invalidateQueries({ queryKey: ['platform-leads'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const removeMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/super-admin/leads/${id}`)
      return id
    },
    onSuccess: () => {
      toast.success('Lead deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['platform-leads'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rows = leadsQuery.data?.data ?? []
  const pageInfo = leadsQuery.data?.pagination ?? null

  // Export pages through the filtered feed (bounded); DataTable sorts the
  // current page internally via each column's `sortAccessor`.
  async function fetchAllForExport(scope, ids) {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/leads', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = leadsResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    // "Export selected" passes the chosen ids — return just those rows.
    if (scope === 'selected' && Array.isArray(ids)) {
      const wanted = new Set(ids.map(String))
      return all.filter((l) => wanted.has(String(l.id)))
    }
    return all
  }

  // Bulk Delete — fan out to the SAME per-lead endpoint the row menu uses.
  const bulkActions = [
    {
      label: 'Delete',
      icon: Trash2,
      danger: true,
      onClick: (ids, ctx) =>
        setBulkDel({ ids, clearSelection: ctx?.clearSelection }),
    },
  ]
  async function runBulkDelete() {
    if (!bulkDel) return
    setBulkRunning(true)
    let ok = 0
    const fails = []
    for (const id of bulkDel.ids) {
      try {
        await api.delete(`/super-admin/leads/${id}`)
        ok += 1
      } catch (e) {
        fails.push(extractApiError(e).message)
      }
    }
    setBulkRunning(false)
    if (ok) toast.success(`${ok} ${ok === 1 ? 'lead' : 'leads'} deleted.`)
    if (fails.length)
      toast.error(
        `${fails.length} failed: ${fails[0]}` +
          (fails.length > 1 ? ` (+${fails.length - 1} more)` : ''),
      )
    await qc.invalidateQueries({ queryKey: ['platform-leads'] })
    bulkDel.clearSelection?.()
    setBulkDel(null)
  }

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        header: 'Lead',
        pinnable: true,
        minWidth: 240,
        sortAccessor: (l) => (l.fullName ?? '').toLowerCase(),
        exportValue: (l) => `${l.fullName ?? ''} <${l.emailId ?? ''}>`,
        render: (l) => (
          <div className="min-w-0 flex items-center gap-3">
            <span className="shrink-0 h-9 w-9 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)] flex items-center justify-center text-xs font-semibold">
              {initials(l.fullName)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                {l.fullName}
              </p>
              <div className="text-xs text-[color:var(--color-ink-muted)] flex items-center gap-1.5 truncate">
                <Mail size={11} className="shrink-0" />
                <span className="truncate" title={l.emailId}>
                  {l.emailId}
                </span>
              </div>
              {l.contactNumber ? (
                <div className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] flex items-center gap-1.5 truncate">
                  <Phone size={11} className="shrink-0" />
                  <span
                    className="truncate"
                    title={formatContactNumber(l.contactNumber)}
                  >
                    {formatContactNumber(l.contactNumber)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: 'company',
        header: 'Company',
        minWidth: 140,
        sortAccessor: (l) => (l.company ?? l.companyName ?? '').toLowerCase(),
        exportValue: (l) => l.company ?? '',
        cellClassName: 'text-sm text-[color:var(--color-ink)]',
        render: (l) => <span className="truncate block">{l.company ?? '—'}</span>,
      },
      {
        key: 'source',
        header: 'Source',
        minWidth: 100,
        sortAccessor: (l) => (l.source ?? '').toLowerCase(),
        exportValue: (l) => SOURCE_LABEL[l.source] ?? l.source ?? '',
        render: (l) => <SourceBadge source={l.source} />,
      },
      {
        key: 'subject',
        header: 'Subject',
        minWidth: 160,
        sortable: false,
        exportValue: (l) => parseEnquiry(l.notes).subject ?? '',
        cellClassName: 'text-sm text-[color:var(--color-ink)]',
        render: (l) => {
          const s = parseEnquiry(l.notes).subject
          return (
            <span className="truncate block" title={s ?? ''}>
              {s ?? '—'}
            </span>
          )
        },
      },
      {
        key: 'description',
        header: 'Description',
        grow: true,
        minWidth: 200,
        sortable: false,
        exportValue: (l) => parseEnquiry(l.notes).message ?? '',
        cellClassName: 'text-sm text-[color:var(--color-ink-muted)]',
        render: (l) => {
          const m = parseEnquiry(l.notes).message
          return (
            <span className="truncate block" title={m ?? ''}>
              {m ?? '—'}
            </span>
          )
        },
      },
      {
        key: 'createdAt',
        header: 'Created',
        minWidth: 150,
        sortAccessor: (l) => (l.createdAt ? new Date(l.createdAt) : null),
        exportValue: (l) => (l.createdAt ? formatDateTime(l.createdAt) : ''),
        cellClassName:
          'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (l) => formatDateTime(l.createdAt),
      },
      {
        key: 'actions',
        header: 'Actions',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (l) => (
          <RowActionsMenu
            label={`Actions for ${l.fullName}`}
            items={[
              {
                key: 'view',
                label: 'View enquiry',
                icon: Eye,
                onClick: () => setViewing(l),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: Trash2,
                danger: true,
                separatorBefore: true,
                onClick: () => setConfirmDelete(l),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  const detail = viewing ? parseEnquiry(viewing.notes) : null

  return (
    <div>
      <Breadcrumb current="Leads" homePath="/super-admin/dashboard" />

      {/* Stacks below `sm` so the action button is never pushed off the right
       *  edge on a phone — same header shape as Companies and Plans. */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <UserPlus size={20} className="text-[color:var(--color-accent-strong)] shrink-0" />
            Leads
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Enquiries captured from the website, Meta, Google, an N8N workflow, or a webhook.
          </p>
        </div>
        <Button
          className="shrink-0 self-start"
          variant="secondary"
          onClick={() => setIntegrationsOpen(true)}
        >
          <Plug size={14} /> Integrations
        </Button>
      </div>

      {/* Search + Source filter — shared FilterRow primitives. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by Full Name, Email ID or Company"
        />
        <FilterRow.Select
          ariaLabel="Source"
          placeholder="All sources"
          value={sourceFilter}
          onChange={(v) => {
            setSourceFilter(v)
            setPage(1)
          }}
          options={sourceOptions}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear
          show={Boolean(search.trim() || sourceFilter)}
          onClick={() => {
            setSearch('')
            setSourceFilter('')
            setPage(1)
          }}
        />
      </FilterRow>

      {/* Server-paginated leads on the shared DataTable. Search + Source
       *  filters above stay page-owned (server params); the row menu opens
       *  the enquiry detail modal / deletes. */}
      <DataTable
        tableId="platform-leads"
        columns={columns}
        rows={rows}
        getRowId={(l) => l.id}
        loading={leadsQuery.isLoading}
        fetching={leadsQuery.isFetching}
        error={
          leadsQuery.isError ? extractApiError(leadsQuery.error).message : null
        }
        onRetry={() => leadsQuery.refetch()}
        searchable={false}
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="leads"
        exportTitle="Leads"
        getExportRows={fetchAllForExport}
        selectable
        bulkActions={bulkActions}
        onSelectAllRecords={async () =>
          (await fetchAllForExport()).map((l) => l.id)
        }
        emptyState={{
          icon: FilterIcon,
          title: 'No leads yet',
          body: 'New enquiries from the website contact form will appear here.',
        }}
      />

      {/* Read-only enquiry detail — contact number, subject + full message */}
      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title="Lead Details"
        size="md"
      >
        {viewing ? (
          <div className="text-sm">
            <DetailRow label="Full Name">{viewing.fullName}</DetailRow>
            <DetailRow label="Email ID">{viewing.emailId}</DetailRow>
            <DetailRow label="Contact Number">
              {formatContactNumber(viewing.contactNumber)}
            </DetailRow>
            <DetailRow label="Company">{viewing.company || '—'}</DetailRow>
            <DetailRow label="Source">
              <SourceBadge source={viewing.source} />
            </DetailRow>
            {/* Status is the one editable field here — the Master Admin
             *  moves a lead along the pipeline from this picker. Saves on
             *  change (a single control doesn't warrant a Save/Cancel pair);
             *  the badge beside it reflects what is currently stored, so the
             *  saved state stays readable while a change is in flight. */}
            <DetailRow label="Status">
              <div className="flex items-center gap-2.5">
                <Select
                  aria-label="Lead status"
                  sizeVariant="sm"
                  fullWidth={false}
                  value={viewing.status ?? 'new'}
                  disabled={statusMutation.isPending}
                  className={
                    statusMutation.isPending
                      ? 'opacity-40 cursor-not-allowed'
                      : 'cursor-pointer'
                  }
                  onChange={(e) =>
                    statusMutation.mutate({ id: viewing.id, status: e.target.value })
                  }
                >
                  {STATUS_ORDER.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </Select>
                <span
                  className={
                    'text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ' +
                    STATUS_TONE[viewing.status]
                  }
                >
                  {STATUS_LABEL[viewing.status]}
                </span>
              </div>
            </DetailRow>
            <DetailRow label="Created">
              {formatDateTime(viewing.createdAt)}
            </DetailRow>
            {/* Subject + Description ALWAYS render, with the same "—"
             *  fallback the table columns use. They were previously
             *  hidden entirely when empty (and Description was labelled
             *  "Message"), so a lead whose table row showed the two
             *  columns opened into a details view without those
             *  sections at all — read as "fields missing from the
             *  details page" (QA-reported). Manual leads have neither
             *  (only website enquiries fold Subject/Message into
             *  notes); the dash states that explicitly instead of
             *  omitting the section. */}
            <DetailRow label="Subject">{detail?.subject ?? '—'}</DetailRow>
            <DetailRow label="Description">
              {detail?.message ? (
                <p className="whitespace-pre-wrap break-words">
                  {detail.message}
                </p>
              ) : (
                '—'
              )}
            </DetailRow>
            {detail?.notes ? (
              <DetailRow label="Notes">
                <p className="whitespace-pre-wrap break-words">
                  {detail.notes}
                </p>
              </DetailRow>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Lead"
        message={
          confirmDelete
            ? `Delete lead "${confirmDelete.fullName}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete Lead"
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) removeMutation.mutate(confirmDelete.id)
        }}
      />

      <ConfirmDialog
        open={bulkDel !== null}
        title={`Delete ${bulkDel?.ids.length ?? 0} ${(bulkDel?.ids.length ?? 0) === 1 ? 'lead' : 'leads'}`}
        message={`Delete ${bulkDel?.ids.length ?? 0} selected ${(bulkDel?.ids.length ?? 0) === 1 ? 'lead' : 'leads'}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={bulkRunning}
        onCancel={() => {
          if (!bulkRunning) setBulkDel(null)
        }}
        onConfirm={runBulkDelete}
      />

      <LeadIntegrationsModal
        open={integrationsOpen}
        onClose={() => setIntegrationsOpen(false)}
      />
    </div>
  )
}

/** One label/value row in the read-only lead detail modal. */
function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3 py-2.5 border-b border-[color:var(--color-rule)] last:border-b-0">
      <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] pt-0.5">
        {label}
      </span>
      <div className="text-sm text-[color:var(--color-ink-strong)] min-w-0 break-words">
        {children}
      </div>
    </div>
  )
}
